'use client';
import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';

interface GenData {
  gen: string;
  label: string;
  execCount: number;
  gsv: number;
  bonusPct: number;
  bonus: number;
  isBreakaway: boolean;
}

const GEN_COLORS: Record<string, string> = {
  G1: 'text-violet-400 bg-violet-500/15 border-violet-500/30',
  G2: 'text-blue-400 bg-blue-500/15 border-blue-500/30',
  G3: 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30',
  G4: 'text-amber-400 bg-amber-500/15 border-amber-500/30',
  G5: 'text-orange-400 bg-orange-500/15 border-orange-500/30',
  G6: 'text-red-400 bg-red-500/15 border-red-500/30',
};

// Mock executive names per generation
const GEN_EXECS: Record<string, { name: string; gsv: number; rank: string }[]> = {
  G1: [
    { name: 'Priya Nambiar', gsv: 31200, rank: 'Blue Diamond' },
    { name: 'Jerome Castillo', gsv: 18700, rank: 'Diamond' },
    { name: 'Aisha Okonkwo', gsv: 1850, rank: 'Prov. Executive' },
  ],
  G2: [
    { name: 'Derek Huang', gsv: 12400, rank: 'Emerald' },
    { name: 'Natalia Ferreira', gsv: 5800, rank: 'Executive' },
    { name: 'Kwame Asante', gsv: 2100, rank: 'BP Ruby' },
    { name: 'Lena Kowalski', gsv: 1640, rank: 'BP Lapis' },
    { name: 'Tomás Varela', gsv: 340, rank: 'Distributor' },
  ],
  G3: [
    { name: 'Fatima Al-Rashid', gsv: 4200, rank: 'Executive' },
    { name: 'Carlos Mendes', gsv: 890, rank: 'BP Lapis' },
    { name: 'Obinna Ezeh', gsv: 490, rank: 'BP Gold' },
  ],
  G4: [
    { name: 'Simone Okafor', gsv: 680, rank: 'BP Gold' },
    { name: 'Ryan Beaumont', gsv: 210, rank: 'Distributor' },
  ],
  G5: [
    { name: 'Pierre Dubois', gsv: 390, rank: 'BP Gold' },
    { name: 'Adaeze Obi', gsv: 240, rank: 'Distributor' },
  ],
  G6: [
    { name: 'Rashida James', gsv: 260, rank: 'Distributor' },
    { name: 'Elias Bergström', gsv: 230, rank: 'Distributor' },
  ],
};

export default function GenerationTreeView({ generationData }: { generationData: GenData[] }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ G1: true, G2: false });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-800">
        <h3 className="text-sm font-semibold text-zinc-300">Generation Tree (G1–G6)</h3>
        <p className="text-[10px] text-zinc-600 mt-0.5">Breakaway executive structure with GSV and commission per generation</p>
      </div>
      <div className="p-3 space-y-1.5">
        {generationData.map(g => {
          const isOpen = expanded[g.gen];
          const colorClass = GEN_COLORS[g.gen] || 'text-zinc-400 bg-zinc-700/40 border-zinc-600/30';
          const execs = GEN_EXECS[g.gen] || [];

          return (
            <div key={`gtv-${g.gen}`} className={`border rounded-xl overflow-hidden ${colorClass.includes('border') ? colorClass.split(' ').find(c => c.startsWith('border-')) : 'border-zinc-700'}`}
              style={{ borderColor: undefined }}>
              <button
                onClick={() => setExpanded(prev => ({ ...prev, [g.gen]: !isOpen }))}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-800/40 transition-colors"
              >
                {isOpen ? <ChevronDown size={13} className="text-zinc-500 flex-shrink-0" /> : <ChevronRight size={13} className="text-zinc-500 flex-shrink-0" />}
                <span className={`text-xs font-bold px-2 py-0.5 rounded border ${colorClass}`}>{g.gen}</span>
                <span className="text-xs font-semibold text-zinc-300 flex-1 text-left">{g.label}</span>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-[10px] text-zinc-500">{g.execCount} execs</span>
                  <span className="text-[10px] font-mono text-zinc-400">{(g.gsv / 1000).toFixed(1)}K GSV</span>
                  <span className={`text-[10px] font-semibold ${g.bonusPct === 5 ? 'text-emerald-400' : 'text-amber-400'}`}>{g.bonusPct}%</span>
                  <span className="text-sm font-bold font-mono text-emerald-400">${g.bonus.toFixed(0)}</span>
                </div>
              </button>

              {isOpen && (
                <div className="border-t border-zinc-800/60 bg-zinc-950/30">
                  {execs.map((exec, i) => (
                    <div key={`gexec-${g.gen}-${i}`} className="flex items-center gap-3 px-4 py-2 border-b border-zinc-800/30 last:border-0 hover:bg-zinc-800/20 transition-colors">
                      <div className="w-1.5 h-1.5 rounded-full bg-zinc-600 flex-shrink-0" />
                      <span className="text-xs text-zinc-300 flex-1">{exec.name}</span>
                      <span className="text-[10px] text-zinc-600">{exec.rank}</span>
                      <span className="text-[10px] font-mono text-zinc-400">{(exec.gsv / 1000).toFixed(1)}K</span>
                      <span className="text-[10px] font-mono font-semibold text-emerald-400">
                        ${(exec.gsv * g.bonusPct / 100).toFixed(0)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}